// ============================================================
// src/components/Navbar.jsx
// Barra de navegación principal de la aplicación
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../services/firebase';

export default function Navbar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [stats, setStats] = useState({ points: 0, level: 1 });

  // Cargar puntos y nivel del usuario desde Firebase
  useEffect(() => {
    if (!user) return;
    const statsRef = ref(database, `users/${user.uid}/stats`);
    get(statsRef).then((snap) => {
      if (snap.exists()) {
        const data = snap.val();
        setStats({
          points: data.totalPoints || 0,
          level: data.level || 1,
        });
      }
    });
  }, [user, location]);

  // Cerrar sesión
  const handleLogout = async () => {
    await signOut(auth);
    setMenuOpen(false);
    navigate('/');
  };

  // Nombre corto del usuario
  const displayName = user?.displayName
    ? user.displayName.split(' ')[0]
    : user?.email?.split('@')[0];

  // Inicial para avatar
  const initial = (user?.displayName || user?.email || 'U')[0].toUpperCase();

  return (
    <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 font-extrabold text-indigo-700 text-xl hover:opacity-80 transition"
        >
          <span><img src="logo.png" alt="" className="w-10 h-10 mx-auto object-contain"/></span>
          <span>OpenMind</span>
        </Link>

        {/* Centro: puntos y nivel (desktop) */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-full px-3 py-1">
            <span className="text-amber-500">⭐</span>
            <span className="text-amber-700 font-bold text-sm">{stats.points} pts</span>
          </div>
          <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-100 rounded-full px-3 py-1">
            <span className="text-purple-500">🎯</span>
            <span className="text-purple-700 font-bold text-sm">Nivel {stats.level}</span>
          </div>
        </div>

        {/* Derecha: avatar y menú */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 rounded-xl px-3 py-1.5 transition"
          >
            {/* Avatar con inicial */}
            <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {initial}
            </div>
            <span className="text-sm font-medium text-gray-700 hidden sm:block">
              {displayName}
            </span>
            <span className="text-gray-400 text-xs">{menuOpen ? '▲' : '▼'}</span>
          </button>

          {/* Menú desplegable */}
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-50">
              {/* Stats en móvil */}
              <div className="md:hidden px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
                <div className="flex justify-between text-xs font-medium">
                  <span className="text-amber-600">⭐ {stats.points} pts</span>
                  <span className="text-purple-600">🎯 Nivel {stats.level}</span>
                </div>
              </div>

              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 transition"
              >
                <span>🏠</span> Inicio
              </Link>
              <Link
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 transition"
              >
                <span>👤</span> Mi perfil
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition border-t"
              >
                <span>🚪</span> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
